<div *ngIf="visible" class="relative z-[100]" aria-labelledby="sql-wizard-title" role="dialog" aria-modal="true">
  <div class="fixed inset-0 bg-secondary-900 bg-opacity-50 backdrop-blur-sm transition-opacity"></div>

  <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
    <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
      <div class="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl">

        <!-- close -->
        <div class="absolute right-0 top-0 pr-4 pt-4">
          <button type="button" (click)="close()" class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none">
            <span class="sr-only">Close</span>
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- header -->
        <div class="px-4 pt-4 lg:px-6">
          <div class="flex items-center gap-3 pr-8">
            <h3 class="text-lg font-semibold leading-6 text-gray-900" id="sql-wizard-title">Sales Qualify</h3>
            <span *ngIf="q && q.score !== null && !isRunning" class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white"
                  [ngClass]="{'bg-icp-low': tierClass === 'low', 'bg-icp-medium': tierClass === 'medium', 'bg-icp-high': tierClass === 'high', 'bg-gray-400': tierClass === 'none'}">
              SQL {{ q.score }}<span *ngIf="tierLabel"> · {{ tierLabel }}</span>
            </span>
          </div>
          <p class="text-sm text-gray-500 mt-1">{{ company?.name }} — qualify this contact's company to a Sales Qualified Lead</p>

          <!-- stage indicator -->
          <ol class="mt-4 flex items-center gap-2 text-xs">
            <li *ngFor="let stage of ['Start', 'Research', 'Your input', 'Result']; let i = index" class="flex items-center gap-2">
              <span class="inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-semibold"
                    [ngClass]="{
                      'bg-[#802cfa] border-[#802cfa] text-white': (view === 'intro' && i === 0) || (view === 'running' && i === 1) || (view === 'input' && i === 2) || (view === 'result' && i === 3),
                      'border-gray-300 text-gray-500': !((view === 'intro' && i === 0) || (view === 'running' && i === 1) || (view === 'input' && i === 2) || (view === 'result' && i === 3))
                    }">{{ i + 1 }}</span>
              <span class="text-gray-600">{{ stage }}</span>
              <span *ngIf="i < 3" class="h-px w-6 bg-gray-200"></span>
            </li>
          </ol>
        </div>

        <!-- ============================================================ INTRO -->
        <div *ngIf="view === 'intro'" class="px-4 py-5 lg:px-6">
          <p *ngIf="q?.status === 'failed'" class="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            The last run failed{{ q?.error ? ': ' + q?.error : '' }}. Your credits were refunded — you can start again.
          </p>

          <p class="text-sm text-gray-700">
            The agent answers four questions about what <strong>{{ company?.name }}</strong> sells and turns them into an SQL score (0–100):
          </p>
          <ol class="mt-3 space-y-1.5 text-sm text-gray-700 list-decimal pl-5">
            <li *ngFor="let question of questions"><span class="font-medium">{{ question.label }}</span> <span class="text-gray-500">— {{ question.help }}</span></li>
          </ol>

          <p class="mt-5 text-sm font-medium text-gray-800">What happens when you start</p>
          <ol class="mt-2 space-y-2">
            <li *ngFor="let step of plannedSteps; let i = index" class="flex gap-3 text-sm">
              <span class="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-50 text-[11px] font-semibold text-[#802cfa]">{{ i + 1 }}</span>
              <span><span class="text-gray-800">{{ step.label }}</span> <span class="text-gray-500">— {{ step.detail }}</span></span>
            </li>
          </ol>

          <p class="mt-5 text-xs text-gray-500">Takes about a minute. Costs <strong>{{ state.creditCost }} credits</strong>; answering questions afterwards is free. You can close this window — the result will appear on the report when it's ready.</p>
        </div>

        <!-- ============================================================ RUNNING -->
        <div *ngIf="view === 'running'" class="px-4 py-5 lg:px-6">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <p class="text-sm text-gray-700">
              <ng-container *ngIf="!isStopping">Working on it — each step below updates live.</ng-container>
              <ng-container *ngIf="isStopping"><strong>Stopping…</strong> finishing the current call, then scoring with what was gathered.</ng-container>
            </p>
            <p class="text-xs tabular-nums text-gray-500" *ngIf="q?.started_at">
              {{ formatDuration(elapsedSeconds) }} elapsed
              <span *ngIf="remainingSeconds !== null"> · {{ remainingSeconds > 0 ? formatDuration(remainingSeconds) + ' left' : 'time limit reached, finishing' }}</span>
            </p>
          </div>

          <!-- research budget -->
          <div *ngIf="q?.started_at" class="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
            <div class="h-full rounded-full transition-all duration-1000"
                 [ngClass]="isStopping ? 'bg-amber-500' : 'bg-[#802cfa]'"
                 [style.width.%]="isStopping ? 100 : budgetUsedPct"></div>
          </div>
          <p class="mt-1 text-[11px] text-gray-400">Research stops automatically after {{ budgetMinutes }} min and scores with what it found. You can stop it any time.</p>

          <!-- interim result -->
          <p *ngIf="q && q.score !== null" class="mt-3 rounded-md bg-violet-50 px-3 py-2 text-xs text-gray-700">
            Score so far: <strong>{{ q.score }}</strong> · {{ answeredCount }} of 4 questions answered — this is what you'll get if you stop now.
          </p>

          <ol class="mt-4 space-y-3">
            <li *ngFor="let step of steps" class="flex gap-3">
              <span class="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                    [ngClass]="{
                      'bg-emerald-500 text-white': step.status === 'done',
                      'bg-[#802cfa] text-white animate-pulse': step.status === 'running' || step.status === 'waiting',
                      'bg-gray-200 text-gray-500': step.status === 'pending' || step.status === 'skipped',
                      'bg-red-500 text-white': step.status === 'failed'
                    }">
                <svg *ngIf="step.status === 'done'" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"/></svg>
                <svg *ngIf="step.status === 'skipped'" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"/></svg>
                <span *ngIf="step.status === 'running' || step.status === 'waiting'" class="h-2 w-2 rounded-full bg-white"></span>
                <span *ngIf="step.status === 'failed'" class="text-[10px] font-bold">!</span>
              </span>
              <div class="min-w-0">
                <p class="text-sm" [ngClass]="{'text-gray-900 font-medium': step.status === 'running' || step.status === 'done', 'text-gray-500': step.status === 'pending' || step.status === 'skipped', 'text-red-700 font-medium': step.status === 'failed'}">
                  {{ step.label }}
                  <span *ngIf="step.status === 'running'" class="ml-1 text-xs font-normal text-[#802cfa]">in progress</span>
                  <span *ngIf="step.status === 'skipped'" class="ml-1 text-xs font-normal text-gray-400">skipped</span>
                </p>
                <p *ngIf="step.detail" class="text-xs text-gray-500 mt-0.5">{{ step.detail }}</p>
              </div>
            </li>
          </ol>

          <p *ngIf="!steps.length" class="mt-4 text-sm text-gray-500">Queued — waiting for a worker to pick it up...</p>
        </div>

        <!-- ============================================================ INPUT -->
        <div *ngIf="view === 'input'" class="px-4 py-5 lg:px-6">
          <!-- compact recap of the research steps -->
          <div class="flex flex-wrap gap-1.5 mb-4" *ngIf="steps.length">
            <span *ngFor="let step of steps" class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]"
                  [ngClass]="{
                    'bg-emerald-50 text-emerald-700': step.status === 'done',
                    'bg-gray-100 text-gray-500': step.status === 'skipped' || step.status === 'pending',
                    'bg-amber-50 text-amber-700': step.status === 'waiting',
                    'bg-red-50 text-red-700': step.status === 'failed'
                  }" [title]="step.detail || ''">
              {{ step.label }}
            </span>
          </div>

          <p *ngIf="finishedEarlyLabel" class="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">{{ finishedEarlyLabel }}</p>

          <p class="text-sm text-gray-700">
            <ng-container *ngIf="pendingCount > 0">
              The agent settled <strong>{{ answeredCount }} of 4</strong> questions with enough confidence. Score so far: <strong>{{ q?.score }}</strong>.
              Please answer the {{ pendingCount === 1 ? 'remaining one' : 'remaining ' + pendingCount }} — your answers always win over the agent's.
            </ng-container>
            <ng-container *ngIf="pendingCount === 0">
              Adjust any answer below; your answers always win over the agent's.
            </ng-container>
          </p>
          <p *ngIf="q?.summary" class="mt-2 text-sm text-gray-600 italic">{{ q?.summary }}</p>

          <form (ngSubmit)="saveAnswers()" class="mt-4 divide-y divide-gray-200">
            <div *ngFor="let question of questions" class="py-4"
                 [ngClass]="{'bg-amber-50/60 -mx-3 px-3 rounded-lg': isPending(question.id)}">
              <div class="flex items-start justify-between gap-4">
                <div class="min-w-0">
                  <p class="text-sm font-medium text-gray-800">
                    {{ question.label }}
                    <span *ngIf="isPending(question.id)" class="ml-1 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">needs your input</span>
                  </p>
                  <p class="text-xs text-gray-500 mt-0.5">{{ question.help }}</p>
                </div>
                <div class="text-right shrink-0" *ngIf="!isPending(question.id)">
                  <p class="text-sm font-semibold text-gray-900">{{ answerLabel(question.id) }} <span class="text-gray-500 font-normal">· +{{ answerFor(question.id)?.points }}</span></p>
                  <p class="text-xs text-gray-500">{{ sourceLabel(question.id) }}</p>
                </div>
              </div>
              <p *ngIf="answerFor(question.id)?.rationale" class="text-xs text-gray-500 mt-2 italic">{{ answerFor(question.id)?.rationale }}</p>

              <div class="mt-3 flex flex-wrap items-center gap-3">
                <ng-container *ngIf="question.id === 'q1'">
                  <label *ngFor="let o of q1Options" class="inline-flex items-center gap-1 text-sm text-gray-700">
                    <input type="radio" name="q1" [value]="o.value" [(ngModel)]="form.q1" class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"> {{ o.label }}
                  </label>
                </ng-container>
                <ng-container *ngIf="question.id === 'q2'">
                  <div class="relative">
                    <span class="pointer-events-none absolute inset-y-0 left-2 flex items-center text-sm text-gray-400">$</span>
                    <input type="number" name="q2_value" min="1" step="100" placeholder="e.g. 25000" [(ngModel)]="form.q2_value"
                           class="w-40 rounded-md border-0 py-1.5 pl-6 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600">
                  </div>
                  <span class="text-xs text-gray-500">per year, for</span>
                  <label *ngFor="let o of q1Options" class="inline-flex items-center gap-1 text-sm text-gray-700">
                    <input type="radio" name="q2_offering" [value]="o.value" [(ngModel)]="form.q2_offering" class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"> {{ o.label }}
                  </label>
                </ng-container>
                <ng-container *ngIf="question.id === 'q3'">
                  <label *ngFor="let o of q3Options" class="inline-flex items-center gap-1 text-sm text-gray-700">
                    <input type="radio" name="q3" [value]="o.value" [(ngModel)]="form.q3" class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"> {{ o.label }}
                  </label>
                </ng-container>
                <ng-container *ngIf="question.id === 'q4'">
                  <label *ngFor="let o of q4Options" class="inline-flex items-center gap-1 text-sm text-gray-700">
                    <input type="radio" name="q4" [value]="o.value" [(ngModel)]="form.q4" class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"> {{ o.label }}
                  </label>
                </ng-container>
              </div>
            </div>
          </form>
        </div>

        <!-- ============================================================ RESULT -->
        <div *ngIf="view === 'result'" class="px-4 py-5 lg:px-6">
          <div class="flex items-center gap-4">
            <div class="flex h-16 w-20 items-center justify-center rounded-lg text-2xl font-semibold text-white !shadow-md"
                 [ngClass]="{'bg-icp-low': tierClass === 'low', 'bg-icp-medium': tierClass === 'medium', 'bg-icp-high': tierClass === 'high', 'bg-gray-400': tierClass === 'none'}">
              {{ q?.score }}
            </div>
            <div>
              <p class="text-base font-semibold text-gray-900">{{ tierLabel || 'Scored' }}</p>
              <p class="text-xs text-gray-500">{{ answeredCount }} of 4 questions answered<span *ngIf="q?.tiers_used?.length"> · sources: {{ q?.tiers_used?.join(', ') }}</span></p>
            </div>
          </div>
          <p *ngIf="finishedEarlyLabel" class="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {{ finishedEarlyLabel }} Re-run the agent for a fuller answer.
          </p>
          <p *ngIf="q?.summary" class="mt-3 text-sm text-gray-700">{{ q?.summary }}</p>

          <div class="flex flex-wrap gap-1.5 mt-4" *ngIf="steps.length">
            <span *ngFor="let step of steps" class="inline-flex items-center rounded-full px-2 py-0.5 text-[11px]"
                  [ngClass]="{'bg-emerald-50 text-emerald-700': step.status === 'done', 'bg-gray-100 text-gray-500': step.status !== 'done'}"
                  [title]="step.detail || ''">{{ step.label }}</span>
          </div>

          <div class="mt-4 divide-y divide-gray-200">
            <div *ngFor="let question of questions" class="py-3">
              <div class="flex items-start justify-between gap-4">
                <p class="text-sm text-gray-800">{{ question.label }}</p>
                <div class="text-right shrink-0">
                  <p class="text-sm font-semibold text-gray-900">{{ answerLabel(question.id) }} <span class="text-gray-500 font-normal">· +{{ answerFor(question.id)?.points ?? 0 }}</span></p>
                  <p class="text-xs text-gray-500">{{ sourceLabel(question.id) }}</p>
                </div>
              </div>
              <p *ngIf="answerFor(question.id)?.rationale" class="text-xs text-gray-500 mt-1 italic">{{ answerFor(question.id)?.rationale }}</p>
              <ul *ngIf="evidenceFor(question.id).length" class="mt-1.5 space-y-1">
                <li *ngFor="let e of evidenceFor(question.id)" class="text-xs text-gray-600">
                  <span class="text-gray-400">[{{ e.tier }}]</span> “{{ e.quote }}”
                  <a *ngIf="e.url" [href]="e.url" target="_blank" rel="noopener" class="text-[#802cfa] hover:underline ml-1">source</a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- ============================================================ FOOTER -->
        <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:items-center sm:px-6 gap-2">
          <ng-container *ngIf="view === 'intro'">
            <button type="button" (click)="start(q?.status === 'failed')" [disabled]="state.starting"
                    class="inline-flex w-full justify-center rounded-md bg-[#802cfa] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-opacity-90 sm:w-auto disabled:opacity-50">
              {{ state.starting ? 'Starting...' : 'Start · ' + state.creditCost + ' credits' }}
            </button>
          </ng-container>

          <ng-container *ngIf="view === 'running'">
            <button type="button" (click)="stopRun()" [disabled]="isStopping"
                    class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-700 shadow-sm ring-1 ring-inset ring-red-200 hover:bg-red-50 sm:w-auto disabled:opacity-60">
              {{ isStopping ? 'Stopping…' : 'Stop & use what was found' }}
            </button>
            <span class="mt-2 block text-xs text-gray-500 sm:mt-0 sm:mr-2">Safe to close — you'll be notified.</span>
          </ng-container>

          <ng-container *ngIf="view === 'input'">
            <button type="button" (click)="saveAnswers()" [disabled]="state.saving"
                    class="inline-flex w-full justify-center rounded-md bg-[#802cfa] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-opacity-90 sm:w-auto disabled:opacity-50">
              {{ state.saving ? 'Saving...' : 'Save answers' }}
            </button>
            <button *ngIf="editing" type="button" (click)="editing = false"
                    class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">
              Back to result
            </button>
          </ng-container>

          <ng-container *ngIf="view === 'result'">
            <button type="button" (click)="startEditing()"
                    class="inline-flex w-full justify-center rounded-md bg-[#802cfa] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-opacity-90 sm:w-auto">
              Edit answers
            </button>
            <button type="button" (click)="start(true)" [disabled]="state.starting"
                    class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50">
              Re-run agent · {{ state.creditCost }} credits
            </button>
          </ng-container>

          <button type="button" (click)="close()"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto sm:mr-auto">
            Close
          </button>
        </div>

      </div>
    </div>
  </div>
</div>
